/**
 * Final ERS Report Component
 * Comprehensive report generation with AI narratives, tables, appendices
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  FileText, Download, Sparkles, ChevronDown, ChevronRight, RefreshCw,
  CheckCircle, Clock, Zap, Flame, Droplets, Factory,
  Target, Leaf, DollarSign, TrendingUp, BarChart3,
  FileCheck, Printer, Calendar, Database, Settings, AlertTriangle
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import { formatReportDate } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to format AI text with section headings in bold
const formatAIText = (text) => {
  if (!text) return [];
  
  // Clean the text first
  let cleaned = text
    .replace(/#{1,6}\s*/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '|||BOLD|||$1|||ENDBOLD|||')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/^[-•●○◦]\s*/gm, '')
    .replace(/^\d+[\.\)]\s*/gm, '')
    .replace(/^>\s*/gm, '')
    .replace(/@/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
  
  // Split into paragraphs
  const paragraphs = cleaned.split(/\n\n+/).filter(p => p.trim().length > 0);
  
  return paragraphs.map((para, idx) => {
    // Check if paragraph starts with a heading pattern
    const headingMatch = para.match(/^([A-Z][^:.\n]{2,50}):?\s*(.*)$/s);
    if (headingMatch && headingMatch[1].length < 50) {
      return {
        type: 'section',
        heading: headingMatch[1].replace(/\|\|\|BOLD\|\|\|/g, '').replace(/\|\|\|ENDBOLD\|\|\|/g, ''),
        content: headingMatch[2]?.replace(/\|\|\|BOLD\|\|\|/g, '').replace(/\|\|\|ENDBOLD\|\|\|/g, '') || ''
      };
    }
    
    // Check for bold markers
    if (para.includes('|||BOLD|||')) {
      const parts = para.split(/\|\|\|BOLD\|\|\||\|\|\|ENDBOLD\|\|\|/);
      return {
        type: 'mixed',
        parts: parts.map((part, i) => ({ text: part, bold: i % 2 === 1 }))
      };
    }
    
    return { type: 'paragraph', content: para };
  });
};

// Render formatted AI text
const RenderAIText = ({ text }) => {
  const formatted = formatAIText(text);
  
  return (
    <div className="space-y-3">
      {formatted.map((item, idx) => {
        if (item.type === 'section') {
          return (
            <div key={idx}>
              <p className="font-bold text-gray-900 mb-1">{item.heading}</p>
              {item.content && <p className="text-gray-700 text-sm leading-relaxed">{item.content}</p>}
            </div>
          );
        }
        if (item.type === 'mixed') {
          return (
            <p key={idx} className="text-gray-700 text-sm leading-relaxed">
              {item.parts.map((part, i) => 
                part.bold ? <strong key={i}>{part.text}</strong> : <span key={i}>{part.text}</span>
              )}
            </p>
          );
        }
        return <p key={idx} className="text-gray-700 text-sm leading-relaxed">{item.content}</p>;
      })}
    </div>
  );
};

const FinalReport = ({ study }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [reportData, setReportData] = useState(null);
  const [narratives, setNarratives] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatingSection, setGeneratingSection] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    executive: true, baseline: true, seu: true, opportunities: true,
    macc: true, roadmap: true, esg: true, conclusion: true, appendix: false
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // PDF generation mode — 'server' uses backend reportlab (crisp vector,
  // matches the other 19 platform PDFs), 'client' uses html2canvas (preserves
  // the on-screen look-and-feel). Persisted to localStorage so demos can flip
  // between them without losing the choice on reload.
  const [pdfMode, setPdfMode] = useState(() => {
    try {
      return window.localStorage.getItem('ers.pdfMode') || 'server';
    } catch {
      return 'server';
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem('ers.pdfMode', pdfMode); } catch {}
  }, [pdfMode]);

  // Tenant logo for the cover page (fetched via same-origin backend proxy so
  // html2canvas can embed it without tainted-canvas / CORS issues).
  const [tenantLogoDataUrl, setTenantLogoDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/tenant-admin/branding/logo-stream`, {
          responseType: 'blob',
        });
        if (cancelled || !r.data) return;
        const reader = new FileReader();
        reader.onloadend = () => { if (!cancelled) setTenantLogoDataUrl(reader.result); };
        reader.readAsDataURL(r.data);
      } catch (err) {
        // 404 (no logo uploaded) or network — fall back to platform logo.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const printContentRef = useRef(null);

  useEffect(() => {
    if (study?.id) {
      fetchReportData();
      fetchNarratives();
    }
  }, [study?.id]);

  const fetchReportData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/api/ers/report/${study.id}/data`, config);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNarratives = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/api/ers/report/${study.id}/narratives`, config);
      setNarratives(response.data || {});
    } catch (error) {
      console.error('Error fetching narratives:', error);
    }
  };

  const generateNarrative = async (section) => {
    setGeneratingSection(section);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/ers/report/${study.id}/generate-narrative`,
        { section },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      setNarratives(prev => ({ ...prev, [section]: response.data }));
      return true;
    } catch (error) {
      console.error('Error generating narrative:', error);
      return false;
    } finally {
      setGeneratingSection(null);
    }
  };

  const generateAllNarratives = async () => {
    setGeneratingAll(true);
    const sections = ['executive', 'baseline', 'seu', 'opportunities', 'scope3', 'macc', 'roadmap', 'conclusion'];
    
    for (const section of sections) {
      setGeneratingSection(section);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/api/ers/report/${study.id}/generate-narrative`,
          { section },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
        );
        setNarratives(prev => ({ ...prev, [section]: response.data }));
      } catch (error) {
        console.error(`Error generating ${section} narrative:`, error);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setGeneratingSection(null);
    setGeneratingAll(false);
    showSuccess('All AI narratives have been generated successfully!');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Main PDF dispatcher — picks between the server-rendered reportlab PDF
  // (crisp vector text, matches the other 19 platform PDFs) and the
  // client-rendered html2canvas PDF (preserves the exact on-screen look
  // including colored metric cards). Choice is stored in pdfMode and
  // persisted to localStorage so demos can flip between them.
  const downloadPDF = async () => {
    if (pdfMode === 'client') {
      await downloadPDFClient();
    } else {
      await downloadPDFServer();
    }
  };

  const downloadPDFServer = async () => {
    if (!study?.id) {
      showWarning('Study not loaded yet.');
      return;
    }
    setGeneratingPDF(true);
    try {
      const response = await axios.get(`${API}/api/ers/report/${study.id}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ERS_Final_Report_${(study?.study_name || 'Report').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF (server):', error);
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      showError('Failed to generate PDF: ' + detail);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Client-side renderer: captures the report preview DOM in PDF-page-sized
  // slices at scale=2 (crisp text, never exceeds browser canvas limits),
  // stitches them into a jsPDF, then stamps a tenant-company-name +
  // 'Page X of Y' footer on every page directly via jsPDF's vector text API.
  const downloadPDFClient = async () => {
    setGeneratingPDF(true);
    const pdfStyleId = 'pdf-override-style';
    try {
      const element = printContentRef.current;
      if (!element) {
        showWarning('Report content not ready. Please open the report preview first.');
        return;
      }
      if (!showReportModal) {
        showWarning('Please open the report preview first, then click PDF.');
        return;
      }

      // Inject styles that:
      // 1. Remove scroll constraints so html2canvas captures full content
      // 2. Prevent tables/rows from being cut between pages
      // 3. Keep section headings with their content
      // 4. Force system fonts for consistent html2canvas rendering
      const style = document.createElement('style');
      style.id = pdfStyleId;
      style.textContent = `
        .pdf-capturing,
        .pdf-capturing > * {
          max-height: none !important;
          overflow: visible !important;
          height: auto !important;
        }
        .pdf-capturing * {
          font-family: Arial, Helvetica, 'Noto Sans', sans-serif !important;
          word-spacing: 0.12em !important;
          letter-spacing: 0.01em !important;
          text-rendering: geometricPrecision !important;
          -webkit-font-smoothing: antialiased !important;
        }
        .pdf-capturing p, .pdf-capturing span, .pdf-capturing li,
        .pdf-capturing td, .pdf-capturing th, .pdf-capturing div {
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: break-word !important;
        }
        .pdf-capturing table {
          page-break-inside: auto !important;
          border-collapse: collapse !important;
        }
        .pdf-capturing tr { page-break-inside: avoid !important; break-inside: avoid !important; }
        .pdf-capturing thead { display: table-header-group !important; }
        .pdf-capturing h2, .pdf-capturing h3, .pdf-capturing h4 {
          page-break-after: avoid !important;
          break-after: avoid !important;
          page-break-inside: avoid !important;
        }
        .pdf-capturing .page-break-after { page-break-after: always !important; break-after: page !important; }
        .pdf-capturing .page-break-before { page-break-before: always !important; break-before: page !important; }
        .pdf-capturing .overflow-x-auto { overflow: visible !important; }
        /* Cover page: use block layout with top padding instead of flex-center.
           Flex-center with min-height: 272mm could push content outside the
           captured slice on some viewport widths. Block + padding keeps the
           logo/title/company-info reliably at the top of the captured slice. */
        .pdf-capturing .cover-page {
          min-height: 8in !important;
          padding-top: 1.2in !important;
          padding-bottom: 0.5in !important;
          box-sizing: border-box !important;
          display: block !important;
          text-align: center !important;
        }
        .pdf-capturing .cover-page img { display: inline-block !important; }
      `;
      document.head.appendChild(style);

      const modalContainer = element.parentElement;
      if (modalContainer) modalContainer.classList.add('pdf-capturing');
      element.classList.add('pdf-capturing');

      element.scrollTop = 0;
      await new Promise(resolve => setTimeout(resolve, 800));

      // Chunked capture — scale=2 on each slice, stitched into one PDF.
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const SCALE = 2;
      const MAX_CANVAS_DIMENSION = 11000;
      const PDF_PAGE_W_MM = 210;
      const PDF_PAGE_H_MM = 297;
      const MARGIN = { top: 10, left: 10, right: 10, bottom: 15 };
      const CONTENT_W_MM = PDF_PAGE_W_MM - MARGIN.left - MARGIN.right;
      const CONTENT_H_MM = PDF_PAGE_H_MM - MARGIN.top - MARGIN.bottom;

      const elW = element.scrollWidth || 900;
      const elH = element.scrollHeight || 2000;
      const pxPerMm = elW / CONTENT_W_MM;
      const sliceHeightFromPage = Math.floor(CONTENT_H_MM * pxPerMm);
      const sliceHeightFromCanvas = Math.floor(MAX_CANVAS_DIMENSION / SCALE);
      const sliceHeightPx = Math.max(200, Math.min(sliceHeightFromPage, sliceHeightFromCanvas));

      const filename = `ERS_Final_Report_${(study?.study_name || 'Report').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const totalSlices = Math.ceil(elH / sliceHeightPx);

      for (let i = 0; i < totalSlices; i++) {
        const yOffset = i * sliceHeightPx;
        const sliceH = Math.min(sliceHeightPx, elH - yOffset);

        const canvas = await html2canvas(element, {
          scale: SCALE,
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: true,
          windowWidth: elW,
          windowHeight: elH,
          y: yOffset,
          height: sliceH,
          scrollY: -yOffset,
          backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        if (i > 0) pdf.addPage();
        const sliceHMm = sliceH / pxPerMm;
        pdf.addImage(imgData, 'JPEG', MARGIN.left, MARGIN.top, CONTENT_W_MM, sliceHMm);
      }

      // Per-page footer: tenant company name (left) + 'Page X of Y' (right).
      const totalPages = pdf.internal.getNumberOfPages();
      const tenantName = (metadata?.company_name || '').substring(0, 80);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(107, 114, 128); // #6b7280
      for (let p = 1; p <= totalPages; p++) {
        pdf.setPage(p);
        if (tenantName) {
          pdf.text(tenantName, MARGIN.left, PDF_PAGE_H_MM - 5);
        }
        pdf.text(`Page ${p} of ${totalPages}`, PDF_PAGE_W_MM - MARGIN.right, PDF_PAGE_H_MM - 5, { align: 'right' });
      }

      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF (client):', error);
      showError('Failed to generate PDF: ' + (error.message || 'Unknown error'));
    } finally {
      const style = document.getElementById(pdfStyleId);
      if (style) style.remove();
      const element = printContentRef.current;
      if (element) {
        element.classList.remove('pdf-capturing');
        if (element.parentElement) element.parentElement.classList.remove('pdf-capturing');
      }
      setGeneratingPDF(false);
    }
  };

  const printReport = () => {
    const element = printContentRef.current;
    if (!element) return;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      showWarning('Please allow popups to print the report.');
      return;
    }

    // Get all stylesheets from the current page
    const styleSheets = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return sheet.href
            ? `<link rel="stylesheet" href="${sheet.href}" />`
            : `<style>${Array.from(sheet.cssRules).map(r => r.cssText).join('\n')}</style>`;
        } catch (e) {
          return sheet.href ? `<link rel="stylesheet" href="${sheet.href}" />` : '';
        }
      })
      .join('\n');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>ERS Final Report - ${study?.study_name || 'Report'}</title>
        ${styleSheets}
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; padding: 20px; }
            @page { margin: 15mm 10mm 20mm 10mm; size: A4; }
            .page-break-before { page-break-before: always; }
            .page-break-after { page-break-after: always; }
            .html2pdf__page-break { display: block; page-break-before: always; }
            .cover-page { min-height: 272mm; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; align-items: center; }
          }
          body { background: white; font-family: Arial, Helvetica, 'Noto Sans', sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; }
          * { word-spacing: 0.08em; letter-spacing: 0.01em; }
          h1, h2, h3, h4 { word-spacing: 0.12em; letter-spacing: 0.02em; }
          .html2pdf__page-break { height: 0; display: block; }
          table { border-collapse: collapse; }
          img { max-width: 100%; }
        </style>
      </head>
      <body>
        ${element.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading report data...</span>
      </div>
    );
  }

  const { metadata, baseline, seu, opportunities, ghg, macc, appendix, completion } = reportData || {};

  // Calculate actual completion based on data availability
  // Note: appendix.assumptions is excluded — it's always-present reference metadata, not study data
  const actualCompletion = {
    completed: 0,
    total: 7,
    sections: []
  };

  if (baseline?.electricity?.total_kwh > 0) { actualCompletion.completed++; actualCompletion.sections.push('Baseline'); }
  if (seu?.total_seus > 0) { actualCompletion.completed++; actualCompletion.sections.push('SEU'); }
  if (opportunities?.total_opportunities > 0) { actualCompletion.completed++; actualCompletion.sections.push('Opportunities'); }
  if (ghg?.total_tco2e > 0) { actualCompletion.completed++; actualCompletion.sections.push('GHG'); }
  if (macc?.macc_data?.length > 0) { actualCompletion.completed++; actualCompletion.sections.push('MACC'); }
  if (appendix?.time_series?.electricity?.length > 0) { actualCompletion.completed++; actualCompletion.sections.push('Time Series'); }
  if (appendix?.onsite_measurements?.electrical?.length > 0) { actualCompletion.completed++; actualCompletion.sections.push('On-Site'); }

  // Section component.
  // The header used to be a <button>, but the "Generate AI" action button
  // lives inside it — nested <button> elements are invalid HTML and cause a
  // React hydration error. The clickable toggle is now only the left side
  // (icon + title) and the chevron on the right; the action button area
  // is a sibling flex region, not a descendant.
  const ReportSection = ({ id, title, icon: Icon, status, children }) => {
    const isExpanded = !!expandedSections[id];
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-4 print:shadow-none print:border print:border-gray-200">
        <div className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors print:bg-white">
          <button
            type="button"
            onClick={() => toggleSection(id)}
            aria-expanded={isExpanded}
            aria-controls={`report-section-${id}`}
            className="flex items-center gap-3 flex-1 text-left bg-transparent border-0 p-0 cursor-pointer"
          >
            <Icon className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            {status === 'complete' && <CheckCircle className="w-4 h-4 text-green-500" />}
            {status === 'pending' && <Clock className="w-4 h-4 text-amber-500" />}
          </button>
          <div className="flex items-center gap-2 print:hidden">
            {!narratives[id]?.narrative && !generatingAll && (
              <button
                type="button"
                onClick={() => generateNarrative(id)}
                disabled={generatingSection === id}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 flex items-center gap-1"
              >
                {generatingSection === id ? (
                  <><RefreshCw className="w-3 h-3 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-3 h-3" /> Generate AI</>
                )}
              </button>
            )}
            {generatingAll && generatingSection === id && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin" /> Generating...
              </span>
            )}
            <button
              type="button"
              onClick={() => toggleSection(id)}
              aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
              className="bg-transparent border-0 p-0 cursor-pointer"
            >
              {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
            </button>
          </div>
        </div>
        {isExpanded && (
          <div id={`report-section-${id}`} className="p-6">
            {narratives[id]?.narrative && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 print:bg-gray-50 print:border-gray-300">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-600 print:text-gray-600" />
                  <span className="text-sm font-semibold text-purple-700 print:text-gray-700">AI-Generated Analysis</span>
                </div>
                <RenderAIText text={narratives[id].narrative} />
              </div>
            )}
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Report Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white print:hidden">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">ERS Final Report</h2>
            <p className="text-indigo-200">{study?.study_name || 'Energy Audit'}</p>
            <p className="text-sm text-indigo-300 mt-2">
              {metadata?.site_name} • Period: {metadata?.study_period || 'N/A'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={generateAllNarratives}
              disabled={generatingAll}
              className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 flex items-center gap-2 disabled:opacity-50"
            >
              {generatingAll ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Generating All...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate All AI</>
              )}
            </button>
            <button
              onClick={() => setShowReportModal(true)}
              className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 flex items-center gap-2"
            >
              <FileCheck className="w-4 h-4" />
              Preview Report
            </button>
          </div>
        </div>
      </div>

      {/* Completion Progress */}
      <div className="bg-white rounded-xl shadow-md p-6 print:hidden">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">Report Completion</h3>
          <span className="text-sm text-gray-600">{actualCompletion.completed} of {actualCompletion.total} data sections complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-indigo-600 to-purple-600 h-3 rounded-full transition-all" 
            style={{ width: `${Math.round((actualCompletion.completed / actualCompletion.total) * 100)}%` }}
          ></div>
        </div>
        <p className="text-xs text-gray-500 mt-2">Sections with data: {actualCompletion.sections.join(', ') || 'None'}</p>
      </div>

      {/* Section 1: Executive Summary */}
      <ReportSection id="executive" title="1. Executive Summary" icon={FileText} status={baseline?.electricity?.total_kwh > 0 ? 'complete' : 'pending'}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <Zap className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{baseline?.electricity?.total_mwh || 0}</p>
            <p className="text-xs text-gray-600">MWh/year</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">₹{opportunities?.total_savings_lakhs || 0}L</p>
            <p className="text-xs text-gray-600">Savings Potential</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <Leaf className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{opportunities?.total_co2_reduction || 0}</p>
            <p className="text-xs text-gray-600">tCO₂e Reduction</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <Target className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{opportunities?.total_opportunities || 0}</p>
            <p className="text-xs text-gray-600">Opportunities</p>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-100"><tr><th className="px-4 py-2 text-left font-semibold">Metric</th><th className="px-4 py-2 text-right font-semibold">Value</th></tr></thead>
          <tbody>
            <tr className="border-b"><td className="px-4 py-2">Annual Electricity</td><td className="px-4 py-2 text-right">{baseline?.electricity?.total_mwh || 0} MWh</td></tr>
            <tr className="border-b"><td className="px-4 py-2">Annual Fuel Energy</td><td className="px-4 py-2 text-right">{baseline?.fuels?.total_gj || 0} GJ</td></tr>
            <tr className="border-b"><td className="px-4 py-2">Total GHG Emissions</td><td className="px-4 py-2 text-right">{ghg?.total_tco2e || 0} tCO₂e</td></tr>
            <tr className="border-b"><td className="px-4 py-2">Identified Opportunities</td><td className="px-4 py-2 text-right">{opportunities?.total_opportunities || 0}</td></tr>
            <tr className="border-b"><td className="px-4 py-2">Total Savings Potential</td><td className="px-4 py-2 text-right font-semibold text-green-600">₹{opportunities?.total_savings_lakhs || 0} Lakhs/yr</td></tr>
            <tr><td className="px-4 py-2">Weighted Avg Payback</td><td className="px-4 py-2 text-right">{opportunities?.avg_payback_months || 0} months</td></tr>
          </tbody>
        </table>
      </ReportSection>

      {/* Section 2: Baseline */}
      <ReportSection id="baseline" title="2. Baseline Energy & Resource Profile" icon={BarChart3} status={baseline?.electricity?.total_kwh > 0 ? 'complete' : 'pending'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3"><Zap className="w-5 h-5 text-yellow-600" /><h4 className="font-semibold text-gray-900">Electricity</h4></div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total Consumption</span><span className="font-medium">{baseline?.electricity?.total_kwh?.toLocaleString() || 0} kWh</span></div>
              <div className="flex justify-between"><span>Intensity</span><span className="font-medium">{baseline?.electricity?.intensity_kwh_per_mt || 0} kWh/MT</span></div>
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3"><Flame className="w-5 h-5 text-orange-600" /><h4 className="font-semibold text-gray-900">Fuels</h4></div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total Energy</span><span className="font-medium">{baseline?.fuels?.total_gj || 0} GJ</span></div>
              {Object.entries(baseline?.fuels?.breakdown || {}).slice(0, 3).map(([fuel, qty]) => (
                <div key={fuel} className="flex justify-between text-gray-600"><span>{fuel}</span><span>{qty?.toLocaleString() || 0}</span></div>
              ))}
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3"><Droplets className="w-5 h-5 text-blue-600" /><h4 className="font-semibold text-gray-900">Water</h4></div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total Usage</span><span className="font-medium">{baseline?.water?.total_kl?.toLocaleString() || 0} KL</span></div>
              <div className="flex justify-between"><span>Intensity</span><span className="font-medium">{baseline?.water?.intensity_kl_per_mt || 0} KL/MT</span></div>
            </div>
          </div>
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3"><Factory className="w-5 h-5 text-gray-600" /><h4 className="font-semibold text-gray-900">Waste</h4></div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total Waste</span><span className="font-medium">{baseline?.waste?.total_mt || 0} MT</span></div>
              <div className="flex justify-between"><span>Intensity</span><span className="font-medium">{baseline?.waste?.intensity_mt_per_mt || 0} MT/MT</span></div>
            </div>
          </div>
        </div>
      </ReportSection>

      {/* Section 3: SEU */}
      <ReportSection id="seu" title="3. Significant Energy Uses (SEUs)" icon={Target} status={seu?.total_seus > 0 ? 'complete' : 'pending'}>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-indigo-50 rounded-lg p-4 text-center"><p className="text-3xl font-bold text-indigo-600">{seu?.total_seus || 0}</p><p className="text-sm text-gray-600">Total SEUs</p></div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center"><p className="text-3xl font-bold text-yellow-600">{seu?.electrical_seus || 0}</p><p className="text-sm text-gray-600">Electrical</p></div>
          <div className="bg-orange-50 rounded-lg p-4 text-center"><p className="text-3xl font-bold text-orange-600">{seu?.thermal_seus || 0}</p><p className="text-sm text-gray-600">Thermal</p></div>
        </div>
        {(seu?.top_seus?.length > 0) && (
          <table className="w-full text-sm">
            <thead className="bg-gray-100"><tr><th className="px-4 py-2 text-left">SEU</th><th className="px-4 py-2 text-left">System</th><th className="px-4 py-2 text-right">Annual Energy</th><th className="px-4 py-2 text-right">Share</th></tr></thead>
            <tbody>
              {(seu?.top_seus || []).map((s, idx) => (
                <tr key={idx} className="border-b"><td className="px-4 py-2 font-medium">{s.seu_id || `SEU-${idx+1}`}</td><td className="px-4 py-2">{s.equipment_type || 'Unknown'}</td><td className="px-4 py-2 text-right">{(s.final_annual_energy || 0).toLocaleString()} {s.final_unit || 'kWh'}</td><td className="px-4 py-2 text-right">{s.contribution_percent?.toFixed(1) || '-'}%</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </ReportSection>

      {/* Section 4: Opportunities */}
      <ReportSection id="opportunities" title="4. Opportunity Register" icon={TrendingUp} status={opportunities?.total_opportunities > 0 ? 'complete' : 'pending'}>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-green-600">{opportunities?.no_capex_count || 0}</p><p className="text-xs text-gray-600">No-CAPEX</p></div>
          <div className="bg-blue-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-blue-600">{opportunities?.low_capex_count || 0}</p><p className="text-xs text-gray-600">Low-CAPEX</p></div>
          <div className="bg-purple-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-purple-600">{opportunities?.capex_count || 0}</p><p className="text-xs text-gray-600">CAPEX</p></div>
          <div className="bg-amber-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-amber-600">{opportunities?.avg_payback_months || 0}mo</p><p className="text-xs text-gray-600">Avg Payback</p></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100"><tr><th className="px-3 py-2 text-left">Opportunity</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-right">Savings ₹/yr</th><th className="px-3 py-2 text-right">CAPEX ₹</th><th className="px-3 py-2 text-right">Payback</th><th className="px-3 py-2 text-right">CO₂ (t/yr)</th></tr></thead>
            <tbody>
              {(opportunities?.all_opportunities || []).map((o, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50"><td className="px-3 py-2 font-medium">{o.opportunity_title}</td><td className="px-3 py-2"><span className={`px-2 py-1 rounded-full text-xs ${o.category === 'No-capex' ? 'bg-green-100 text-green-700' : o.category === 'Low-capex' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{o.category || 'Capex'}</span></td><td className="px-3 py-2 text-right text-green-600 font-medium">₹{((o.annual_cost_savings || 0) / 100000).toFixed(1)}L</td><td className="px-3 py-2 text-right">₹{((o.capex || 0) / 100000).toFixed(1)}L</td><td className="px-3 py-2 text-right">{(o.simple_payback_months || 0).toFixed(1)} mo</td><td className="px-3 py-2 text-right">{(o.annual_co2_reduction || 0).toFixed(1)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>

      {/* Section 5: Scope 3 Screening */}
      <ReportSection id="scope3" title="5. Scope 3 Value Chain Screening" icon={TrendingUp} status={narratives.scope3 ? 'complete' : 'pending'}>
        {narratives.scope3 ? (
          <>
            <div className="prose prose-sm max-w-none text-gray-800 mb-6">
              <ReactMarkdown>
                {typeof narratives.scope3 === 'string' 
                  ? narratives.scope3 
                  : narratives.scope3.narrative || JSON.stringify(narratives.scope3)}
              </ReactMarkdown>
            </div>
            
            {/* Raw Scope 3 Data */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-cyan-900 mb-3">Category Breakdown</h4>
              <Scope3DataDisplay studyId={study?.id} />
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-xs text-orange-900 font-semibold">⚠️ Disclaimer</p>
              <p className="text-xs text-orange-800 mt-1">
                Scope 3 values are screening-grade estimates intended for prioritization, not external assurance.
              </p>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <button
              onClick={() => generateNarrative('scope3')}
              disabled={generatingSection === 'scope3'}
              className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Sparkles className="w-5 h-5" />
              {generatingSection === 'scope3' ? 'Generating...' : 'Generate Scope 3 Analysis'}
            </button>
          </div>
        )}
      </ReportSection>

      {/* Section 6: MACC */}
      <ReportSection id="macc" title="6. MACC - Decarbonisation Priority" icon={Leaf} status={macc?.macc_data?.length > 0 ? 'complete' : 'pending'}>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-green-600">{macc?.by_classification?.no_regret || 0}</p><p className="text-xs text-gray-600">No-Regret</p></div>
          <div className="bg-blue-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-blue-600">{macc?.by_classification?.low_cost || 0}</p><p className="text-xs text-gray-600">Low-Cost</p></div>
          <div className="bg-amber-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-amber-600">{macc?.by_classification?.medium_cost || 0}</p><p className="text-xs text-gray-600">Medium-Cost</p></div>
          <div className="bg-red-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-red-600">{macc?.by_classification?.high_cost || 0}</p><p className="text-xs text-gray-600">High-Cost</p></div>
        </div>
        {(macc?.macc_data?.length > 0) && (
          <table className="w-full text-sm">
            <thead className="bg-gray-100"><tr><th className="px-3 py-2 text-left">Opportunity</th><th className="px-3 py-2 text-right">Lifetime CO₂ (t)</th><th className="px-3 py-2 text-right">MAC (₹/tCO₂)</th><th className="px-3 py-2 text-left">Classification</th></tr></thead>
            <tbody>
              {(macc?.macc_data || []).map((m, idx) => (
                <tr key={idx} className="border-b"><td className="px-3 py-2">{m.title}</td><td className="px-3 py-2 text-right">{m.lifetime_co2?.toLocaleString()}</td><td className="px-3 py-2 text-right">{m.mac_per_tco2?.toLocaleString()}</td><td className="px-3 py-2"><span className={`px-2 py-1 rounded-full text-xs ${m.classification === 'No-regret' ? 'bg-green-100 text-green-700' : m.classification === 'Low-cost' ? 'bg-blue-100 text-blue-700' : m.classification === 'Medium-cost' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{m.classification}</span></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </ReportSection>

      {/* Section 7: Roadmap */}
      <ReportSection id="roadmap" title="7. Implementation Roadmap" icon={Calendar} status="complete">
        <div className="space-y-4">
          <div className="border-l-4 border-green-500 pl-4 py-2"><h4 className="font-semibold text-gray-900">Phase 1: Quick Wins (0-6 months)</h4><p className="text-sm text-gray-600 mt-1">Focus: No-CAPEX and low-investment opportunities including leak fixing, equipment tuning, and operational improvements.</p></div>
          <div className="border-l-4 border-blue-500 pl-4 py-2"><h4 className="font-semibold text-gray-900">Phase 2: Efficiency CAPEX (6-18 months)</h4><p className="text-sm text-gray-600 mt-1">Focus: Medium investment with strong ROI including VFD installations, boiler economizers, and LED retrofits.</p></div>
          <div className="border-l-4 border-purple-500 pl-4 py-2"><h4 className="font-semibold text-gray-900">Phase 3: Structural Changes (18-36 months)</h4><p className="text-sm text-gray-600 mt-1">Focus: Strategic initiatives including renewable energy integration, fuel switching, and process optimization.</p></div>
        </div>
      </ReportSection>

      {/* Section 8: ESG */}
      <ReportSection id="esg" title="8. ESG & Regulatory Alignment" icon={FileCheck} status="complete">
        <table className="w-full text-sm">
          <thead className="bg-gray-100"><tr><th className="px-4 py-2 text-left">Framework</th><th className="px-4 py-2 text-center">Status</th><th className="px-4 py-2 text-left">Notes</th></tr></thead>
          <tbody>
            <tr className="border-b"><td className="px-4 py-2">BRSR (Principle 6)</td><td className="px-4 py-2 text-center"><CheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td><td className="px-4 py-2 text-gray-600">Energy & emissions data ready</td></tr>
            <tr className="border-b"><td className="px-4 py-2">SGX Climate Reporting</td><td className="px-4 py-2 text-center"><CheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td><td className="px-4 py-2 text-gray-600">Scope 1 & 2 calculated</td></tr>
            <tr className="border-b"><td className="px-4 py-2">CBAM</td><td className="px-4 py-2 text-center"><CheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td><td className="px-4 py-2 text-gray-600">Embedded emissions available</td></tr>
            <tr><td className="px-4 py-2">SBTi Inputs</td><td className="px-4 py-2 text-center"><CheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td><td className="px-4 py-2 text-gray-600">Baseline and reduction pathway</td></tr>
          </tbody>
        </table>
      </ReportSection>

      {/* Section 9: Conclusion */}
      <ReportSection id="conclusion" title="9. Conclusion & Next Steps" icon={FileText} status="complete">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h4 className="font-semibold text-indigo-900 mb-3">Recommended Actions</h4>
          <div className="text-sm text-gray-700 space-y-2">
            <p>1. Present findings to management and approve priority opportunities</p>
            <p>2. Deploy IoT monitoring for continuous energy tracking</p>
            <p>3. Integrate findings into ESG reporting workflows</p>
            <p>4. Schedule quarterly review meetings to track progress</p>
          </div>
        </div>
      </ReportSection>

      {/* Section 10: Appendices */}
      <ReportSection id="appendix" title="10. Appendices" icon={Database} status={appendix?.time_series?.electricity?.length > 0 ? 'complete' : 'pending'}>
        {/* Appendix A: Time Series Data */}
        <div className="mb-8">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Database className="w-4 h-4" /> Appendix A: Time Series Data</h4>
          
          {/* Electricity */}
          {appendix?.time_series?.electricity?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">A.1 Electricity Consumption</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Period</th><th className="px-2 py-1 border text-right">kWh</th><th className="px-2 py-1 border text-right">Max Demand (kVA)</th><th className="px-2 py-1 border text-right">Power Factor</th><th className="px-2 py-1 border text-right">Bill (₹)</th></tr></thead>
                  <tbody>
                    {appendix.time_series.electricity.map((e, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{e.month || e.period}</td><td className="px-2 py-1 border text-right">{(e.consumed_units_kwh || e.kwh_consumption || 0).toLocaleString()}</td><td className="px-2 py-1 border text-right">{e.md_kva || e.max_demand_kva || '-'}</td><td className="px-2 py-1 border text-right">{e.power_factor || '-'}</td><td className="px-2 py-1 border text-right">{(e.bill_amount || 0).toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fuels */}
          {appendix?.time_series?.fuels?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">A.2 Fuel Consumption</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Period</th><th className="px-2 py-1 border">Fuel Type</th><th className="px-2 py-1 border text-right">Quantity</th><th className="px-2 py-1 border">Unit</th><th className="px-2 py-1 border text-right">Bill (₹)</th></tr></thead>
                  <tbody>
                    {appendix.time_series.fuels.map((f, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{f.month || f.period}</td><td className="px-2 py-1 border">{f.fuel_type}</td><td className="px-2 py-1 border text-right">{(f.quantity || 0).toLocaleString()}</td><td className="px-2 py-1 border">{f.unit}</td><td className="px-2 py-1 border text-right">{(f.bill_amount || 0).toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Water */}
          {appendix?.time_series?.water?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">A.3 Water Consumption</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Period</th><th className="px-2 py-1 border">Source</th><th className="px-2 py-1 border text-right">Quantity (KL)</th><th className="px-2 py-1 border text-right">Bill (₹)</th></tr></thead>
                  <tbody>
                    {appendix.time_series.water.map((w, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{w.month || w.period}</td><td className="px-2 py-1 border">{w.source || w.source_type}</td><td className="px-2 py-1 border text-right">{(w.fresh_m3 || w.quantity_kl || 0).toLocaleString()}</td><td className="px-2 py-1 border text-right">{(w.bill_amount || 0).toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Production */}
          {appendix?.time_series?.production?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">A.4 Production Data</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Period</th><th className="px-2 py-1 border">Product</th><th className="px-2 py-1 border text-right">Quantity</th><th className="px-2 py-1 border">Unit</th></tr></thead>
                  <tbody>
                    {appendix.time_series.production.map((p, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{p.month || p.period}</td><td className="px-2 py-1 border">{p.product_id}</td><td className="px-2 py-1 border text-right">{(p.quantity || 0).toLocaleString()}</td><td className="px-2 py-1 border">{p.basis || p.unit}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Waste */}
          {appendix?.time_series?.waste?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">A.5 Waste Generation</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Period</th><th className="px-2 py-1 border">Waste Type</th><th className="px-2 py-1 border text-right">Quantity (MT)</th><th className="px-2 py-1 border">Disposal</th></tr></thead>
                  <tbody>
                    {appendix.time_series.waste.map((w, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{w.month || w.period}</td><td className="px-2 py-1 border">{w.waste_type}</td><td className="px-2 py-1 border text-right">{(w.quantity_mt || w.quantity || 0).toLocaleString()}</td><td className="px-2 py-1 border">{w.disposal_method}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Appendix B: On-Site Measurements */}
        <div className="mb-8">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Settings className="w-4 h-4" /> Appendix B: On-Site Measurement Results</h4>
          
          {appendix?.onsite_measurements?.electrical?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">B.1 Electrical Measurements</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Equipment</th><th className="px-2 py-1 border text-right">Load (kW)</th><th className="px-2 py-1 border text-right">Current (A)</th><th className="px-2 py-1 border text-right">Power Factor</th><th className="px-2 py-1 border text-right">THD (%)</th></tr></thead>
                  <tbody>
                    {appendix.onsite_measurements.electrical.map((e, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{e.equipment_id || 'Equipment'}</td><td className="px-2 py-1 border text-right">{e.measured_kw || '-'}</td><td className="px-2 py-1 border text-right">{e.measured_amps || '-'}</td><td className="px-2 py-1 border text-right">{e.power_factor || '-'}</td><td className="px-2 py-1 border text-right">{e.thd_percent || '-'}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {appendix?.onsite_measurements?.thermal?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">B.2 Thermal Measurements</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Equipment</th><th className="px-2 py-1 border text-right">Stack Temp (°C)</th><th className="px-2 py-1 border text-right">Excess Air (%)</th><th className="px-2 py-1 border text-right">O₂ (%)</th><th className="px-2 py-1 border text-right">CO₂ (%)</th></tr></thead>
                  <tbody>
                    {appendix.onsite_measurements.thermal.map((t, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{t.equipment_id || 'Equipment'}</td><td className="px-2 py-1 border text-right">{t.stack_temp_c || '-'}</td><td className="px-2 py-1 border text-right">{t.excess_air_percent || '-'}</td><td className="px-2 py-1 border text-right">{t.o2_percent || '-'}</td><td className="px-2 py-1 border text-right">{t.co2_percent || '-'}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {appendix?.onsite_measurements?.observations?.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-2">B.3 Field Observations</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Category</th><th className="px-2 py-1 border">Observation</th><th className="px-2 py-1 border">Severity</th></tr></thead>
                  <tbody>
                    {appendix.onsite_measurements.observations.map((o, i) => (
                      <tr key={i}><td className="px-2 py-1 border">{o.category || 'General'}</td><td className="px-2 py-1 border">{o.observation}</td><td className="px-2 py-1 border"><span className={`px-2 py-0.5 rounded text-xs ${o.severity === 'High' ? 'bg-red-100 text-red-700' : o.severity === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{o.severity || 'Low'}</span></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Appendix C: Assumptions */}
        <div className="mb-8">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Appendix C: Assumptions & Calculation Basis</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-gray-200">
              <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Category</th><th className="px-2 py-1 border">Assumption</th><th className="px-2 py-1 border">Source</th></tr></thead>
              <tbody>
                {(appendix?.assumptions || []).map((a, i) => (
                  <tr key={i}><td className="px-2 py-1 border font-medium">{a.category}</td><td className="px-2 py-1 border">{a.assumption}</td><td className="px-2 py-1 border text-gray-600">{a.source}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Appendix D: Data Confidence */}
        <div className="mb-4">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Appendix D: Data Confidence Levels</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-gray-200">
              <thead className="bg-gray-100"><tr><th className="px-2 py-1 border">Data Area</th><th className="px-2 py-1 border">Confidence</th><th className="px-2 py-1 border">Reason</th></tr></thead>
              <tbody>
                {(appendix?.confidence_levels || []).map((c, i) => (
                  <tr key={i}><td className="px-2 py-1 border font-medium">{c.area}</td><td className="px-2 py-1 border"><span className={`px-2 py-0.5 rounded text-xs ${c.confidence === 'High' ? 'bg-green-100 text-green-700' : c.confidence.includes('Medium') ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{c.confidence}</span></td><td className="px-2 py-1 border text-gray-600">{c.reason}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ReportSection>

      {/* Report Preview Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Final ERS Report Preview</h3>
              <div className="flex items-center gap-2">
                <button onClick={printReport} className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/30 flex items-center gap-1"><Printer className="w-4 h-4" /> Print</button>
                <select
                  value={pdfMode}
                  onChange={(e) => setPdfMode(e.target.value)}
                  disabled={generatingPDF}
                  title="Choose PDF rendering mode"
                  className="px-2 py-1.5 bg-white/20 text-white rounded-lg text-xs font-medium hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/40 cursor-pointer"
                >
                  <option value="server" className="text-gray-900">Server render (clean print)</option>
                  <option value="client" className="text-gray-900">Client render (live look)</option>
                </select>
                <button onClick={downloadPDF} disabled={generatingPDF} className="px-3 py-1.5 bg-white text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-50 flex items-center gap-1">{generatingPDF ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}{generatingPDF ? 'Generating...' : 'PDF'}</button>
                <button onClick={() => setShowReportModal(false)} className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/30">Close</button>
              </div>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8 bg-white" ref={printContentRef}>
              {/* Cover Page */}
              <div className="text-center mb-12 pb-8 border-b-2 border-gray-200 page-break-after cover-page">
                {tenantLogoDataUrl ? (
                  <img
                    src={tenantLogoDataUrl}
                    alt="Company logo"
                    className="max-h-20 mx-auto mb-6 object-contain"
                  />
                ) : (
                  <div
                    className="mx-auto mb-6 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
                    style={{ width: '220px', height: '80px' }}
                    aria-label="No company logo uploaded"
                  >
                    <span className="text-sm italic text-gray-400">Your logo here</span>
                  </div>
                )}
                <h1 className="text-3xl font-bold text-gray-900 mb-2">ENERGY & RESOURCE STUDY</h1>
                <h2 className="text-xl text-gray-600 mb-8">Final Report</h2>
                <div className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
                  <p className="text-lg font-semibold text-gray-900">{metadata?.company_name}</p>
                  <p className="text-gray-600">{metadata?.site_name}</p>
                  <p className="text-sm text-gray-500 mt-3">Study Period: {metadata?.study_period}</p>
                  <p className="text-sm text-gray-500">Generated: {formatReportDate(new Date())}</p>
                </div>
              </div>
              {/* 1. Executive Summary */}
              <div className="mb-10 page-break-before">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">1. Executive Summary</h2>
                {narratives.executive?.narrative && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-indigo-500">
                    <RenderAIText text={narratives.executive.narrative} />
                  </div>
                )}
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-4 py-2 text-left border font-bold">Metric</th><th className="px-4 py-2 text-right border font-bold">Value</th></tr></thead>
                  <tbody>
                    <tr><td className="px-4 py-2 border">Annual Electricity</td><td className="px-4 py-2 text-right border">{baseline?.electricity?.total_mwh || 0} MWh</td></tr>
                    <tr><td className="px-4 py-2 border">Annual Fuel Energy</td><td className="px-4 py-2 text-right border">{baseline?.fuels?.total_gj || 0} GJ</td></tr>
                    <tr><td className="px-4 py-2 border">Total GHG Emissions</td><td className="px-4 py-2 text-right border">{ghg?.total_tco2e || 0} tCO₂e</td></tr>
                    <tr><td className="px-4 py-2 border font-semibold">Total Savings Potential</td><td className="px-4 py-2 text-right border font-semibold text-green-600">₹{opportunities?.total_savings_lakhs || 0} Lakhs/yr</td></tr>
                    <tr><td className="px-4 py-2 border">Weighted Avg Payback</td><td className="px-4 py-2 text-right border">{opportunities?.avg_payback_months || 0} months</td></tr>
                  </tbody>
                </table>
              </div>

              {/* 2. GHG Summary */}
              <div className="mb-10 pdf-no-break">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">2. GHG Emissions Summary</h2>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-orange-50 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-orange-600">{ghg?.scope1_tco2e || 0}</p><p className="text-sm text-gray-600">Scope 1 (tCO₂e)</p></div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-blue-600">{ghg?.scope2_tco2e || 0}</p><p className="text-sm text-gray-600">Scope 2 (tCO₂e)</p></div>
                  <div className="bg-cyan-50 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-cyan-600">{ghg?.scope3_tco2e || 0}</p><p className="text-sm text-gray-600">Scope 3 (tCO₂e)</p></div>
                  <div className="bg-gray-100 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-gray-900">{ghg?.total_tco2e || 0}</p><p className="text-sm text-gray-600">Total (tCO₂e)</p></div>
                </div>
              </div>

              {/* 3. Significant Energy Uses */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">3. Significant Energy Uses (SEUs)</h2>
                {narratives.seu?.narrative && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-indigo-500">
                    <RenderAIText text={narratives.seu.narrative} />
                  </div>
                )}
                {seu?.top_seus?.length > 0 && (
                  <table className="w-full text-sm border border-gray-200">
                    <thead className="bg-gray-100"><tr><th className="px-3 py-2 text-left border font-bold">SEU</th><th className="px-3 py-2 text-left border font-bold">System</th><th className="px-3 py-2 text-right border font-bold">Annual Energy</th><th className="px-3 py-2 text-right border font-bold">Share</th></tr></thead>
                    <tbody>
                      {seu.top_seus.map((s, idx) => (
                        <tr key={idx}><td className="px-3 py-2 border">{s.seu_id || `SEU-${idx+1}`}</td><td className="px-3 py-2 border">{s.equipment_type}</td><td className="px-3 py-2 text-right border">{(s.final_annual_energy || 0).toLocaleString()} kWh</td><td className="px-3 py-2 text-right border">{s.contribution_percent?.toFixed(1)}%</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* 4. Opportunity Register */}
              <div className="mb-10 page-break-before">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">4. Opportunity Register</h2>
                {narratives.opportunities?.narrative && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-indigo-500">
                    <RenderAIText text={narratives.opportunities.narrative} />
                  </div>
                )}
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-3 py-2 text-left border font-bold">Opportunity</th><th className="px-3 py-2 text-left border font-bold">Type</th><th className="px-3 py-2 text-right border font-bold">Savings ₹/yr</th><th className="px-3 py-2 text-right border font-bold">CAPEX ₹</th><th className="px-3 py-2 text-right border font-bold">Payback</th><th className="px-3 py-2 text-right border font-bold">CO₂ (t/yr)</th></tr></thead>
                  <tbody>
                    {(opportunities?.all_opportunities || []).map((o, idx) => (
                      <tr key={idx}><td className="px-3 py-2 border">{o.opportunity_title}</td><td className="px-3 py-2 border">{o.category}</td><td className="px-3 py-2 text-right border">₹{((o.annual_cost_savings || 0) / 100000).toFixed(1)}L</td><td className="px-3 py-2 text-right border">₹{((o.capex || 0) / 100000).toFixed(1)}L</td><td className="px-3 py-2 text-right border">{(o.simple_payback_months || 0).toFixed(1)} mo</td><td className="px-3 py-2 text-right border">{(o.annual_co2_reduction || 0).toFixed(1)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 5. Scope 3 Value Chain Screening */}
              <div className="mb-10 pdf-no-break">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-cyan-500">5. Scope 3 Value Chain Screening</h2>
                {narratives.scope3 && (
                  <div className="bg-cyan-50 p-4 rounded-lg mb-4 border-l-4 border-cyan-500">
                    <RenderAIText text={narratives.scope3.narrative || narratives.scope3} />
                  </div>
                )}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-900 font-semibold">⚠️ Disclaimer</p>
                  <p className="text-xs text-orange-800 mt-1">
                    Scope 3 values are screening-grade estimates intended for prioritization, not external assurance.
                  </p>
                </div>
              </div>

              {/* 6. MACC Analysis */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">6. MACC Analysis</h2>
                {narratives.macc?.narrative && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-indigo-500">
                    <RenderAIText text={narratives.macc.narrative} />
                  </div>
                )}
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-3 py-2 text-left border font-bold">Opportunity</th><th className="px-3 py-2 text-right border font-bold">Lifetime CO₂ (t)</th><th className="px-3 py-2 text-right border font-bold">MAC (₹/tCO₂)</th><th className="px-3 py-2 text-left border font-bold">Classification</th></tr></thead>
                  <tbody>
                    {(macc?.macc_data || []).map((m, idx) => (
                      <tr key={idx}><td className="px-3 py-2 border">{m.title}</td><td className="px-3 py-2 text-right border">{m.lifetime_co2?.toLocaleString()}</td><td className="px-3 py-2 text-right border">{m.mac_per_tco2?.toLocaleString()}</td><td className="px-3 py-2 border">{m.classification}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 7. Implementation Roadmap */}
              <div className="mb-10 pdf-no-break">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">7. Implementation Roadmap</h2>
                {narratives.roadmap?.narrative && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-indigo-500">
                    <RenderAIText text={narratives.roadmap.narrative} />
                  </div>
                )}
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-4 py-2 text-left border font-bold">Phase</th><th className="px-4 py-2 text-left border font-bold">Timeline</th><th className="px-4 py-2 text-left border font-bold">Focus Areas</th></tr></thead>
                  <tbody>
                    <tr><td className="px-4 py-2 border font-medium">Phase 1</td><td className="px-4 py-2 border">0-6 months</td><td className="px-4 py-2 border">Quick wins: Leak fixing, tuning, operational improvements</td></tr>
                    <tr><td className="px-4 py-2 border font-medium">Phase 2</td><td className="px-4 py-2 border">6-18 months</td><td className="px-4 py-2 border">Efficiency CAPEX: VFDs, economizers, LED retrofits</td></tr>
                    <tr><td className="px-4 py-2 border font-medium">Phase 3</td><td className="px-4 py-2 border">18-36 months</td><td className="px-4 py-2 border">Structural: Renewables, fuel switching, process optimization</td></tr>
                  </tbody>
                </table>
              </div>

              {/* Appendix A: Time Series Data */}
              <div className="mb-10 page-break-before">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">Appendix A: Time Series Data</h2>
                
                {appendix?.time_series?.electricity?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-800 mb-2">A.1 Electricity Consumption</h3>
                    <table className="w-full text-xs border border-gray-200">
                      <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Period</th><th className="px-2 py-1 border text-right font-bold">kWh</th><th className="px-2 py-1 border text-right font-bold">MD (kVA)</th><th className="px-2 py-1 border text-right font-bold">PF</th><th className="px-2 py-1 border text-right font-bold">Bill (₹)</th></tr></thead>
                      <tbody>
                        {appendix.time_series.electricity.map((e, i) => (
                          <tr key={i}><td className="px-2 py-1 border">{e.month}</td><td className="px-2 py-1 border text-right">{(e.consumed_units_kwh || 0).toLocaleString()}</td><td className="px-2 py-1 border text-right">{e.md_kva || '-'}</td><td className="px-2 py-1 border text-right">{e.power_factor || '-'}</td><td className="px-2 py-1 border text-right">{(e.bill_amount || 0).toLocaleString()}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {appendix?.time_series?.fuels?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-800 mb-2">A.2 Fuel Consumption</h3>
                    <table className="w-full text-xs border border-gray-200">
                      <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Period</th><th className="px-2 py-1 border font-bold">Fuel Type</th><th className="px-2 py-1 border text-right font-bold">Quantity</th><th className="px-2 py-1 border font-bold">Unit</th><th className="px-2 py-1 border text-right font-bold">Bill (₹)</th></tr></thead>
                      <tbody>
                        {appendix.time_series.fuels.map((f, i) => (
                          <tr key={i}><td className="px-2 py-1 border">{f.month}</td><td className="px-2 py-1 border">{f.fuel_type}</td><td className="px-2 py-1 border text-right">{(f.quantity || 0).toLocaleString()}</td><td className="px-2 py-1 border">{f.unit}</td><td className="px-2 py-1 border text-right">{(f.bill_amount || 0).toLocaleString()}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {appendix?.time_series?.water?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-800 mb-2">A.3 Water Consumption</h3>
                    <table className="w-full text-xs border border-gray-200">
                      <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Period</th><th className="px-2 py-1 border font-bold">Source</th><th className="px-2 py-1 border text-right font-bold">Quantity (KL)</th><th className="px-2 py-1 border text-right font-bold">Bill (₹)</th></tr></thead>
                      <tbody>
                        {appendix.time_series.water.map((w, i) => (
                          <tr key={i}><td className="px-2 py-1 border">{w.month}</td><td className="px-2 py-1 border">{w.source || w.source_type}</td><td className="px-2 py-1 border text-right">{(w.fresh_m3 || w.quantity_kl || 0).toLocaleString()}</td><td className="px-2 py-1 border text-right">{(w.bill_amount || 0).toLocaleString()}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {appendix?.time_series?.production?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-800 mb-2">A.4 Production Data</h3>
                    <table className="w-full text-xs border border-gray-200">
                      <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Period</th><th className="px-2 py-1 border font-bold">Product</th><th className="px-2 py-1 border text-right font-bold">Quantity</th><th className="px-2 py-1 border font-bold">Unit</th></tr></thead>
                      <tbody>
                        {appendix.time_series.production.map((p, i) => (
                          <tr key={i}><td className="px-2 py-1 border">{p.month}</td><td className="px-2 py-1 border">{p.product_id}</td><td className="px-2 py-1 border text-right">{(p.quantity || 0).toLocaleString()}</td><td className="px-2 py-1 border">{p.basis || p.unit}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {appendix?.time_series?.waste?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-800 mb-2">A.5 Waste Generation</h3>
                    <table className="w-full text-xs border border-gray-200">
                      <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Period</th><th className="px-2 py-1 border font-bold">Waste Type</th><th className="px-2 py-1 border text-right font-bold">Quantity (MT)</th><th className="px-2 py-1 border font-bold">Disposal</th></tr></thead>
                      <tbody>
                        {appendix.time_series.waste.map((w, i) => (
                          <tr key={i}><td className="px-2 py-1 border">{w.month}</td><td className="px-2 py-1 border">{w.waste_type}</td><td className="px-2 py-1 border text-right">{(w.quantity_mt || 0).toLocaleString()}</td><td className="px-2 py-1 border">{w.disposal_method}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Appendix B: On-Site Measurements */}
              {(appendix?.onsite_measurements?.electrical?.length > 0 || appendix?.onsite_measurements?.thermal?.length > 0 || appendix?.onsite_measurements?.observations?.length > 0) && (
                <div className="mb-10 page-break-before">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">Appendix B: On-Site Measurements</h2>
                  
                  {appendix?.onsite_measurements?.electrical?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-gray-800 mb-2">B.1 Electrical Measurements</h3>
                      <table className="w-full text-xs border border-gray-200">
                        <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Equipment</th><th className="px-2 py-1 border text-right font-bold">Load (kW)</th><th className="px-2 py-1 border text-right font-bold">Current (A)</th><th className="px-2 py-1 border text-right font-bold">PF</th><th className="px-2 py-1 border text-right font-bold">THD (%)</th></tr></thead>
                        <tbody>
                          {appendix.onsite_measurements.electrical.map((e, i) => (
                            <tr key={i}><td className="px-2 py-1 border">{e.equipment_id}</td><td className="px-2 py-1 border text-right">{e.measured_kw || '-'}</td><td className="px-2 py-1 border text-right">{e.measured_amps || '-'}</td><td className="px-2 py-1 border text-right">{e.power_factor || '-'}</td><td className="px-2 py-1 border text-right">{e.thd_percent || '-'}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {appendix?.onsite_measurements?.thermal?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-gray-800 mb-2">B.2 Thermal Measurements</h3>
                      <table className="w-full text-xs border border-gray-200">
                        <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Equipment</th><th className="px-2 py-1 border text-right font-bold">Stack Temp (°C)</th><th className="px-2 py-1 border text-right font-bold">Excess Air (%)</th><th className="px-2 py-1 border text-right font-bold">O₂ (%)</th><th className="px-2 py-1 border text-right font-bold">CO₂ (%)</th></tr></thead>
                        <tbody>
                          {appendix.onsite_measurements.thermal.map((t, i) => (
                            <tr key={i}><td className="px-2 py-1 border">{t.equipment_id}</td><td className="px-2 py-1 border text-right">{t.stack_temp_c || '-'}</td><td className="px-2 py-1 border text-right">{t.excess_air_percent || '-'}</td><td className="px-2 py-1 border text-right">{t.o2_percent || '-'}</td><td className="px-2 py-1 border text-right">{t.co2_percent || '-'}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {appendix?.onsite_measurements?.observations?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-gray-800 mb-2">B.3 Field Observations</h3>
                      <table className="w-full text-xs border border-gray-200">
                        <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Category</th><th className="px-2 py-1 border font-bold">Observation</th><th className="px-2 py-1 border font-bold">Severity</th></tr></thead>
                        <tbody>
                          {appendix.onsite_measurements.observations.map((o, i) => (
                            <tr key={i}><td className="px-2 py-1 border">{o.category}</td><td className="px-2 py-1 border">{o.observation}</td><td className="px-2 py-1 border">{o.severity}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Appendix C: Assumptions */}
              <div className="mb-10 page-break-before">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">Appendix C: Assumptions & Calculation Basis</h2>
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Category</th><th className="px-2 py-1 border font-bold">Assumption</th><th className="px-2 py-1 border font-bold">Source</th></tr></thead>
                  <tbody>
                    {(appendix?.assumptions || []).map((a, i) => (
                      <tr key={i}><td className="px-2 py-1 border font-medium">{a.category}</td><td className="px-2 py-1 border">{a.assumption}</td><td className="px-2 py-1 border text-gray-600">{a.source}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Appendix D: Data Confidence */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">Appendix D: Data Confidence Levels</h2>
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 border font-bold">Data Area</th><th className="px-2 py-1 border font-bold">Confidence</th><th className="px-2 py-1 border font-bold">Reason</th></tr></thead>
                  <tbody>
                    {(appendix?.confidence_levels || []).map((c, i) => (
                      <tr key={i}><td className="px-2 py-1 border font-medium">{c.area}</td><td className="px-2 py-1 border">{c.confidence}</td><td className="px-2 py-1 border text-gray-600">{c.reason}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer — platform attribution + AI disclaimer (matches the server-side PDF) */}
              <div className="mt-12 pt-6 border-t-2 border-gray-200 text-center text-xs text-gray-500 pdf-no-break">
                <p className="font-medium mb-2">Generated by Ergoniq Platform — a product of SAARV Solutions</p>
                <p className="italic text-gray-400 mb-1">This report was generated using AI-assisted tools and may contain inaccuracies. Please verify critical information before acting on it.</p>
                <p className="mt-2 text-gray-400">Confidential — For internal use only</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Scope 3 Data Display Component
const Scope3DataDisplay = ({ studyId }) => {
  const [scope3Data, setScope3Data] = useState(null);

  useEffect(() => {
    const fetchScope3 = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/scope3/hotspot/${studyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setScope3Data(res.data);
      } catch (error) {
        console.error('Error fetching Scope 3:', error);
      }
    };

    if (studyId) {
      fetchScope3();
    }
  }, [studyId]);

  if (!scope3Data) return <p className="text-sm text-gray-600">Loading Scope 3 data...</p>;

  return (
    <table className="w-full text-sm">
      <thead className="bg-cyan-100">
        <tr>
          <th className="px-3 py-2 text-left font-bold">Category</th>
          <th className="px-3 py-2 text-right font-bold">Emissions (tCO₂e)</th>
          <th className="px-3 py-2 text-right font-bold">Share %</th>
          <th className="px-3 py-2 text-center font-bold">Materiality</th>
        </tr>
      </thead>
      <tbody>
        {scope3Data.categories?.map((cat, idx) => (
          <tr key={idx} className="border-b">
            <td className="px-3 py-2">{cat.category}</td>
            <td className="px-3 py-2 text-right font-medium">{cat.emissions_tco2e}</td>
            <td className="px-3 py-2 text-right">{cat.share_pct}%</td>
            <td className="px-3 py-2 text-center">
              <span className={`px-2 py-1 rounded text-xs font-medium bg-${cat.color}-100 text-${cat.color}-700`}>
                {cat.materiality}
              </span>
            </td>
          </tr>
        ))}
        <tr className="bg-cyan-100 font-bold">
          <td className="px-3 py-2">Total Scope 3</td>
          <td className="px-3 py-2 text-right">{scope3Data.total_scope3_emissions} tCO₂e</td>
          <td className="px-3 py-2 text-right">100%</td>
          <td className="px-3 py-2"></td>
        </tr>
      </tbody>
    </table>
  );
};

export default FinalReport;
